import { useMemo } from "react";
import { Controller, useForm, useWatch, type Resolver } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";

import { WFPButton } from "@/components/ui/button";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

import Loading from "../../../../components/Loading";
import { OFFER_TYPES } from "../../../../constants";
import { getCurrencySymbolFromCache } from "../../../../utils";
import { isDemandLine, requiresDeliveryCost } from "../../../../utils/delivery-cost";
import { getLocalisedName, getQualityLocalName } from "../../../../utils/i18n";
import { Field, buildDeliveryLocationOptions } from "../../../DirectOfferDetails/OfferForm";
import { Offer } from "../OfferCard/interfaces";
import { OfferSummary } from "../OfferSummary";
import { createCounterOfferSchema, type CounterOfferFormValues } from "./schema";

interface ExchangeLocationAssignment {
  trader_organization: number;
  types: number[];
}

export interface ExchangeLocation {
  id: number;
  name: string;
  location: number;
  physical_address: string;
  assignments: ExchangeLocationAssignment[];
}

interface AggregatorOption {
  id: number;
  location_details?: { name?: string };
}

interface CounterOfferModalProps {
  offer: Offer;
  onValidate: (offer: Offer, values: CounterOfferFormValues) => Promise<unknown>;
  onCancel: () => void;
  customExchangeLocations: ExchangeLocation[];
  aggregators: AggregatorOption[];
  trader: { id?: number; physical_address?: string; name?: string };
  administrativeAreas: { id: number; name: string }[];
  exchangeLocationTypes: { id: number; name: string }[];
  initialValues: CounterOfferFormValues;
}

export const CounterOfferModal = ({
  offer,
  onValidate,
  onCancel,
  customExchangeLocations,
  aggregators,
  trader,
  administrativeAreas,
  exchangeLocationTypes,
  initialValues,
}: CounterOfferModalProps) => {
  const { t } = useTranslation();

  const resolver = useMemo(
    () =>
      zodResolver(
        createCounterOfferSchema(offer, t)
      ) as unknown as Resolver<CounterOfferFormValues>,
    [offer, t]
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CounterOfferFormValues>({ resolver, defaultValues: initialValues });
  const deliveryLocation = useWatch({ control, name: "delivery_location" });
  // A demand line's location and delivery cost are the demand's, negotiated on the demand.
  const demandLine = isDemandLine(offer);
  const showDeliveryCost = !demandLine && requiresDeliveryCost(deliveryLocation?.value);

  const isDirectOffer = offer.offer_type === OFFER_TYPES.DIRECT_OFFER;
  const currency = getCurrencySymbolFromCache();
  const unit = offer.unit_of_measure?.code;
  const quality = t(getQualityLocalName(offer.quality));
  const commodity = getLocalisedName(offer.buy_commodity);

  const aggregator = aggregators.find(item => item.id === offer.tagged_aggregator?.id);
  const deliveryLocationOptions = useMemo(
    () =>
      buildDeliveryLocationOptions(
        t,
        trader,
        aggregator?.location_details?.name,
        customExchangeLocations,
        administrativeAreas.map(area => ({ value: area.id, label: area.name })),
        exchangeLocationTypes
      ),
    [t, trader, aggregator, customExchangeLocations, administrativeAreas, exchangeLocationTypes]
  );

  const onSubmit = handleSubmit(values => onValidate(offer, values));

  // Direct offers open before their full deposit (with the quantity ceiling) has loaded.
  const isDepositPending = isDirectOffer && !offer.deposit?.quantity;

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {t("counterOfferModal.heading", { aggregator_name: offer.tagged_aggregator?.name })}
          </DialogTitle>
        </DialogHeader>

        <OfferSummary offer={offer} showAggregator={false} />

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {isDepositPending ? (
            <Loading isVisible />
          ) : (
            <>
              <Controller
                control={control}
                name="minimum_quantity"
                render={({ field, fieldState }) => (
                  <Field
                    id="minimum_quantity"
                    label={`${t(
                      isDirectOffer
                        ? "counterOfferModal.quantity"
                        : "counterOfferModal.minimum_quantity",
                      { commodity, unit, quality }
                    )} *`}
                    errorMessage={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      id="minimum_quantity"
                      type="number"
                      min={0}
                      step="0.01"
                      value={field.value ?? ""}
                      placeholder={t("counterOfferModal.quantityPlaceholder")}
                      aria-invalid={!!fieldState.error}
                    />
                  </Field>
                )}
              />

              {!isDirectOffer && (
                <Controller
                  control={control}
                  name="maximum_quantity"
                  render={({ field, fieldState }) => (
                    <Field
                      id="maximum_quantity"
                      label={t("counterOfferModal.maximum_quantity", { commodity, unit, quality })}
                      errorMessage={fieldState.error?.message}
                    >
                      <Input
                        {...field}
                        id="maximum_quantity"
                        type="number"
                        min={0}
                        step="0.01"
                        value={field.value ?? ""}
                        placeholder={t("counterOfferModal.quantityPlaceholder")}
                        aria-invalid={!!fieldState.error}
                      />
                    </Field>
                  )}
                />
              )}

              <Controller
                control={control}
                name="price"
                render={({ field, fieldState }) => (
                  <Field
                    id="price"
                    label={`${t("counterOfferModal.price", { unit, currency })} *`}
                    errorMessage={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      id="price"
                      type="number"
                      min={0}
                      step="0.01"
                      value={field.value ?? ""}
                      placeholder={t("counterOfferModal.pricePlaceholder")}
                      aria-invalid={!!fieldState.error}
                    />
                  </Field>
                )}
              />

              <Controller
                control={control}
                name="delivery_location"
                render={({ field, fieldState }) => {
                  const selectedIndex = deliveryLocationOptions.findIndex(
                    option => JSON.stringify(option.value) === JSON.stringify(field.value?.value)
                  );
                  return (
                    <Field
                      id="delivery_location"
                      label={`${t("counterOfferModal.exchangeLocation")} *`}
                      errorMessage={fieldState.error?.message}
                    >
                      <Select
                        value={selectedIndex >= 0 ? String(selectedIndex) : undefined}
                        onValueChange={value =>
                          field.onChange(deliveryLocationOptions[Number(value)])
                        }
                        disabled={demandLine}
                      >
                        <SelectTrigger
                          id="delivery_location"
                          className="w-full"
                          aria-invalid={!!fieldState.error}
                        >
                          <SelectValue
                            placeholder={t("counterOfferModal.deliveryLocationPlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          {deliveryLocationOptions.map((option, index) => (
                            <SelectItem key={index} value={String(index)}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  );
                }}
              />

              {showDeliveryCost && (
                <Controller
                  control={control}
                  name="delivery_cost"
                  render={({ field, fieldState }) => (
                    <Field
                      id="delivery_cost"
                      label={`${t("counterOfferModal.deliveryCost", { currency })} *`}
                      errorMessage={fieldState.error?.message}
                    >
                      <Input
                        {...field}
                        id="delivery_cost"
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        value={field.value ?? ""}
                        placeholder={t("counterOfferModal.deliveryCostPlaceholder")}
                        aria-invalid={!!fieldState.error}
                      />
                    </Field>
                  )}
                />
              )}

              <Controller
                control={control}
                name="planned_exchange_time"
                render={({ field, fieldState }) => (
                  <Field
                    id="planned_exchange_time"
                    label={`${t("counterOfferModal.exchangeDateTime")} *`}
                    errorMessage={fieldState.error?.message}
                  >
                    <DateTimePicker
                      id="planned_exchange_time"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t("counterOfferModal.exchangeDateTimePlaceholder")}
                      timeAriaLabel={t("counterOfferModal.exchangeDateTime")}
                      minHour={4}
                      maxHour={18}
                      invalid={!!fieldState.error}
                    />
                  </Field>
                )}
              />

              <Controller
                control={control}
                name="text"
                render={({ field }) => (
                  <Field id="text" label={t("counterOfferModal.comments")}>
                    <Textarea {...field} id="text" value={field.value ?? ""} />
                  </Field>
                )}
              />
            </>
          )}

          <DialogFooter>
            <WFPButton type="button" variant="secondary" onClick={onCancel}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="submit" disabled={isSubmitting || isDepositPending}>
              {isSubmitting && <Spinner className="size-4" />}
              {t("counterOfferModal.submitButtonText")}
            </WFPButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
